"use client";

import type { CContactFormMap } from "@/lib/api/cms-catalog";
import { useT } from "@/lib/i18n/context";
import { cDedicatedContactFormTranslations } from "./CDedicatedContactForm.i18n";
import styles from "./MapView.module.css";

/**
 * MapView — mapa interaktywna pod formularzem kontaktowym.
 *
 * **Strategia implementacji:** `<iframe>` z URL embed providera. Bez JS deps
 * (react-leaflet / @react-google-maps/api) — działa natywnie, zero CSP issues,
 * zero API key dla podstawowego embed (Google Maps embed bez klucza działa
 * z basic params, OSM embed jest publiczny).
 *
 * **Dispatch per `api`:**
 *   - `google` -> Google Maps Street View embed (greyscale przez CSS filter
 *     gdy admin zaznaczył; note_html + button "Wskazówki dojazdu" pod mapą)
 *   - `openstreetmap` -> OSM embed.openstreetmap.org z marker pin
 *
 * **Trade-offs:**
 *   - Iframe = brak full kontroli (custom markers, popups, overlays nieosiągalne)
 *   - Plus: bundle size 0, no JS init, działa od razu z SSR (po hydration
 *     iframe loaduje się natywnie)
 *   - Dla bardziej zaawansowanych map (custom marker icons, popups, geocoding)
 *     w kolejnej iteracji można dorzucić `react-leaflet` (~30KB gzip).
 *
 * **Loading state:** native iframe loading (browser pokazuje empty while loading).
 * Add `loading="lazy"` żeby visible viewport decydował kiedy ładujemy mapę
 * (perf optymalizacja — formularz może być page header, mapa daleko w dół).
 */
export function MapView({ map }: { map: CContactFormMap }) {
  if (map.lat == null || map.lon == null) {
    return (
      <div className={styles.error}>
        Mapa jest skonfigurowana, ale brakuje koordynatów (szerokość/długość
        geograficzna). Ustaw je w panelu administracyjnym.
      </div>
    );
  }

  if (map.api === "google") {
    return <GoogleMapView map={map} />;
  }

  return <OpenStreetMapView map={map} />;
}

/* ─── Google Maps ─────────────────────────────────────────────────────── */

function GoogleMapView({
  map,
}: {
  map: Extract<CContactFormMap, { api: "google" }>;
}) {
  const t = useT(cDedicatedContactFormTranslations);
  /* Google Maps embed: minimalne params dla basic location pin.
     `q={lat},{lon}` — placeholder query bez API key. Output `embed` = iframe.
     Hl=pl dla polskiej lokalizacji UI (etykiety map "Wskazówki", "Pełny widok"). */
  const src = `https://maps.google.com/maps?q=${map.lat},${map.lon}&hl=pl&z=15&output=embed`;

  return (
    <div className={styles.root}>
      <div
        className={`${styles.iframeWrap} ${map.greyscale ? styles.greyscale : ""}`}
      >
        <iframe
          src={src}
          className={styles.iframe}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="Mapa lokalizacji"
          allowFullScreen
        />
      </div>

      {(map.note_html || map.directions_url) && (
        <div className={styles.info}>
          {map.note_html && (
            <div
              className={styles.note}
              dangerouslySetInnerHTML={{ __html: map.note_html }}
            />
          )}
          {map.directions_url && (
            <a
              href={map.directions_url}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.directionsLink}
            >
              {t.map.directionsLink}
            </a>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── OpenStreetMap ───────────────────────────────────────────────────── */

function OpenStreetMapView({
  map,
}: {
  map: Extract<CContactFormMap, { api: "openstreetmap" }>;
}) {
  const t = useT(cDedicatedContactFormTranslations);
  /* OSM embed: bbox + marker. Bbox wyliczamy z lat/lon + offset (default
     ~0.005 ° = ~500m vertikalnie). `marker` param pokazuje pin w środku.
     `layer=mapnik` to default OSM tiles (jest jeszcze cyclemap, transportmap).

     Zoom param NIE jest natywnie w OSM embed URL — kontrolujemy przez wielkość
     bbox. Większy bbox = mniejszy zoom, mniejszy = większy zoom. Mapping
     `map.zoom` na bbox offset: offset = 0.05 / 2^(zoom-10). Default zoom=15
     -> offset = 0.05 / 32 ≈ 0.0016 (~150m vertical). */
  const zoom = map.zoom ?? 15;
  const offset = 0.05 / Math.pow(2, Math.max(0, zoom - 10));
  const bbox = [
    map.lon! - offset * 1.5,
    map.lat! - offset,
    map.lon! + offset * 1.5,
    map.lat! + offset,
  ].join(",");

  const src =
    `https://www.openstreetmap.org/export/embed.html` +
    `?bbox=${encodeURIComponent(bbox)}` +
    `&layer=mapnik` +
    `&marker=${map.lat},${map.lon}`;

  return (
    <div className={styles.root}>
      <div className={styles.iframeWrap}>
        <iframe
          src={src}
          className={styles.iframe}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="Mapa lokalizacji"
        />
      </div>
      <a
        href={`https://www.openstreetmap.org/?mlat=${map.lat}&mlon=${map.lon}#map=${zoom}/${map.lat}/${map.lon}`}
        target="_blank"
        rel="noopener noreferrer"
        className={styles.osmLink}
      >
        {t.map.openInOpenStreetMap}
      </a>
    </div>
  );
}
